import React, { useMemo } from "react";
import { Layers, CheckCircle2 } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { fmtINR } from "../lib/api";

/**
 * Phase 229 — "Which tax heads are you paying?"
 *
 * Shown whenever a property owes money under MORE THAN ONE tax head (Property Tax + User Charges + Water Charges ...),
 * for the citizen (portal + public page), the tax collector and the supervisor alike. Every head is listed with its
 * balance due; under each head every year is its own line (the user chose to pick the year too) with a tick and an
 * amount box capped at that line's balance (net payable today). Everything starts ticked with the full balance, so
 * paying everything stays one tap; the payer unticks or lowers what they do not want to pay now.
 *
 * `lines`  = open demand lines ({demand_id, tax_head_id, tax_head_name, tax_head, fy_label, usage_label,
 *             balance_before|balance_amount, penalty_new, discount, net_payable_full})
 * `value`  = { [demand_id]: { checked: bool, amount: string } }  (controlled; see `initialSelection`)
 * `caps`   = optional { [demand_id]: number } lower caps (e.g. bulk-import remaining balance)
 */
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const lineMax = (ln, caps) => {
  const base = ln.net_payable_full != null ? r2(ln.net_payable_full) : r2(ln.balance_amount ?? ln.balance_before);
  const cap = caps?.[ln.demand_id];
  return cap != null ? Math.min(base, r2(cap)) : base;
};
/** every line ticked with its full balance (the default the user asked for) */
export const initialSelection = (lines, caps) => {
  const out = {};
  (lines || []).forEach((ln) => { out[ln.demand_id] = { checked: true, amount: String(lineMax(ln, caps)) }; });
  return out;
};
/** -> { allocations: [{demand_id, amount}], total, selectedCount, lineCount, headsSelected, headsTotal, errors: [] } */
export const summarizeSelection = (lines, value, caps) => {
  const allocations = [];
  const errors = [];
  const headsAll = new Set();
  const headsSel = new Set();
  (lines || []).forEach((ln) => {
    headsAll.add(ln.tax_head_id || ln.tax_head_name || ln.tax_head);
    const v = value?.[ln.demand_id];
    if (!v?.checked) return;
    const amt = r2(v.amount);
    const max = lineMax(ln, caps);
    if (!(amt > 0)) errors.push(`${ln.tax_head}${ln.fy_label ? ` ${ln.fy_label}` : ""}: enter an amount`);
    else if (amt > max + 0.004) errors.push(`${ln.tax_head}${ln.fy_label ? ` ${ln.fy_label}` : ""}: more than its balance (${fmtINR(max)})`);
    else { allocations.push({ demand_id: ln.demand_id, amount: amt }); headsSel.add(ln.tax_head_id || ln.tax_head_name || ln.tax_head); }
  });
  const total = r2(allocations.reduce((s, a) => s + a.amount, 0));
  return { allocations, total, selectedCount: allocations.length, lineCount: (lines || []).length, headsSelected: headsSel.size, headsTotal: headsAll.size, errors };
};

export const groupByHead = (lines) => {
  const groups = [];
  const idx = {};
  (lines || []).forEach((ln) => {
    const key = ln.tax_head_id || ln.tax_head_name || ln.tax_head;
    if (idx[key] == null) { idx[key] = groups.length; groups.push({ key, name: ln.tax_head_name || ln.tax_head, tax_code: ln.tax_code, lines: [] }); }
    groups[idx[key]].lines.push(ln);
  });
  return groups;
};

export function TaxHeadPicker({ lines, value, onChange, caps, disabled, testIdPrefix = "heads", compact = false }) {
  const groups = useMemo(() => groupByHead(lines), [lines]);
  const summary = useMemo(() => summarizeSelection(lines, value, caps), [lines, value, caps]);
  const set = (next) => onChange?.(next);

  const toggleLine = (ln, checked) => {
    const cur = value?.[ln.demand_id] || {};
    set({ ...value, [ln.demand_id]: { checked, amount: checked ? (cur.amount && r2(cur.amount) > 0 ? cur.amount : String(lineMax(ln, caps))) : cur.amount ?? "" } });
  };
  const toggleHead = (g, checked) => {
    const next = { ...value };
    g.lines.forEach((ln) => {
      const cur = next[ln.demand_id] || {};
      next[ln.demand_id] = { checked, amount: checked ? (cur.amount && r2(cur.amount) > 0 ? cur.amount : String(lineMax(ln, caps))) : cur.amount ?? "" };
    });
    set(next);
  };
  const setAmount = (ln, raw) => {
    const max = lineMax(ln, caps);
    let v = raw;
    if (raw !== "" && Number(raw) > max + 0.004) v = String(max);   // never above the line's balance
    set({ ...value, [ln.demand_id]: { checked: true, amount: v } });
  };
  const setFull = (ln) => set({ ...value, [ln.demand_id]: { checked: true, amount: String(lineMax(ln, caps)) } });

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-picker`} data-selected={summary.selectedCount} data-total={summary.total}>
      {!compact && (
        <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2">
          <Layers className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <p className="text-xs text-muted-foreground">
            This property has dues under <span className="font-medium text-foreground">{summary.headsTotal} tax heads</span>. Tick the head(s) and year(s) being paid
            and enter the amount for each — up to its balance. Only the ticked lines are marked paid; the rest stay due.
          </p>
        </div>
      )}
      {groups.map((g) => {
        const sel = g.lines.filter((ln) => value?.[ln.demand_id]?.checked);
        const all = sel.length === g.lines.length;
        const some = sel.length > 0 && !all;
        const headBal = r2(g.lines.reduce((s, ln) => s + lineMax(ln, caps), 0));
        const headSel = r2(g.lines.reduce((s, ln) => s + (value?.[ln.demand_id]?.checked ? r2(value[ln.demand_id].amount) : 0), 0));
        return (
          <div key={g.key} className={`rounded-xl border overflow-hidden ${sel.length ? "border-primary/40" : ""}`} data-testid={`${testIdPrefix}-head-${g.tax_code || g.key}`}>
            <label className="flex items-center gap-3 px-3 py-2.5 bg-muted/30 cursor-pointer min-h-[44px]">
              <Checkbox checked={all ? true : some ? "indeterminate" : false} disabled={disabled}
                onCheckedChange={(v) => toggleHead(g, v === true)} data-testid={`${testIdPrefix}-head-check-${g.tax_code || g.key}`} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold leading-tight">{g.name}</span>
                <span className="block text-[11px] text-muted-foreground">Balance due {fmtINR(headBal)}{g.lines.length > 1 ? ` · ${g.lines.length} years` : ""}</span>
              </span>
              <span className="text-sm font-semibold tabular-nums" data-testid={`${testIdPrefix}-head-amount-${g.tax_code || g.key}`}>{sel.length ? fmtINR(headSel) : <span className="text-muted-foreground font-normal text-xs">not paying</span>}</span>
            </label>
            <div className="divide-y">
              {g.lines.map((ln) => {
                const v = value?.[ln.demand_id] || { checked: false, amount: "" };
                const max = lineMax(ln, caps);
                const amt = r2(v.amount);
                const full = v.checked && Math.abs(amt - max) < 0.005;
                const over = v.checked && amt > max + 0.004;
                return (
                  <div key={ln.demand_id} className={`flex items-center gap-3 px-3 py-2 ${v.checked ? "" : "opacity-70"}`} data-testid={`${testIdPrefix}-line-${ln.demand_id}`}>
                    <Checkbox checked={!!v.checked} disabled={disabled} onCheckedChange={(c) => toggleLine(ln, c === true)}
                      data-testid={`${testIdPrefix}-line-check-${ln.demand_id}`} aria-label={`Pay ${ln.tax_head} ${ln.fy_label || ""}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-tight">
                        <span className="font-medium">{ln.fy_label || "Current year"}</span>
                        {ln.usage_label && <span className="text-muted-foreground"> · {ln.usage_label}</span>}
                      </p>
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        Balance {fmtINR(max)}
                        {ln.penalty_new > 0 && <span className="text-amber-700"> · penalty +{fmtINR(ln.penalty_new)}</span>}
                        {ln.discount > 0 && <span className="text-emerald-700"> · discount −{fmtINR(ln.discount)} on full</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {v.checked && !full && !over && amt > 0 && (
                        <button type="button" className="text-[11px] text-primary underline-offset-2 hover:underline" onClick={() => setFull(ln)} disabled={disabled}
                          data-testid={`${testIdPrefix}-line-full-${ln.demand_id}`}>full</button>
                      )}
                      <Input type="number" inputMode="decimal" min="0" max={max} step="0.01" value={v.checked ? v.amount : ""} disabled={disabled || !v.checked}
                        placeholder={v.checked ? "0.00" : "—"} onChange={(e) => setAmount(ln, e.target.value)}
                        className={`w-28 h-10 text-right tabular-nums ${over ? "border-destructive" : full ? "border-emerald-400" : ""}`}
                        aria-invalid={over} data-testid={`${testIdPrefix}-line-amount-${ln.demand_id}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" data-testid={`${testIdPrefix}-summary`}>
        <div className="text-xs text-muted-foreground">
          <span data-testid={`${testIdPrefix}-summary-heads`}>{summary.headsSelected} of {summary.headsTotal} heads</span>
          {summary.lineCount > summary.headsTotal && <span> · {summary.selectedCount} of {summary.lineCount} lines</span>}
          {summary.headsSelected > 0 && summary.headsSelected < summary.headsTotal && <Badge variant="outline" className="ml-2 text-[10px]">others stay due</Badge>}
        </div>
        <div className="text-right">
          <p className="text-[11px] text-muted-foreground">Paying now</p>
          <p className="text-lg font-semibold tabular-nums inline-flex items-center gap-1" data-testid={`${testIdPrefix}-summary-total`}>
            {summary.errors.length === 0 && summary.total > 0 && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}{fmtINR(summary.total)}
          </p>
        </div>
      </div>
      {summary.errors.length > 0 && (
        <ul className="text-xs text-destructive space-y-0.5" data-testid={`${testIdPrefix}-errors`}>
          {summary.errors.map((e, i) => <li key={i}>• {e}</li>)}
        </ul>
      )}
    </div>
  );
}

export default TaxHeadPicker;
